import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
} from "@react-pdf/renderer";
import { useTheme } from "@mui/material";

const InvoicePDF = ({ weekData, vendorData, logoSrc }) => {
  const theme = useTheme(); // ✅ Access theme

  const invoiceDate = new Date().toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  // Dynamic styles using theme
  const styles = {
    page: {
      padding: 40,
      fontSize: 10,
      fontFamily: "Helvetica",
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
    },
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: 30,
    },
    logoSection: {
      flexDirection: "column",
    },
    logo: {
      width: 40,
      height: 40,
      marginBottom: 10,
    },
    invoiceTitle: {
      fontSize: 28,
      fontWeight: "bold",
      color: theme.palette.text.primary,
    },
    dateSection: {
      alignItems: "flex-end",
    },
    dateLabel: {
      fontSize: 9,
      color: theme.palette.text.secondary,
    },
    dateValue: {
      fontSize: 14,
      fontWeight: "bold",
      marginTop: 2,
      color: theme.palette.text.primary,
    },
    divider: {
      borderBottom: 1,
      borderColor: theme.palette.divider,
      marginVertical: 20,
    },
    addressSection: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: 20,
    },
    addressBlock: {
      flex: 1,
    },
    addressLabel: {
      fontSize: 9,
      color: theme.palette.text.secondary,
      marginBottom: 5,
      textTransform: "uppercase",
    },
    companyName: {
      fontSize: 14,
      fontWeight: "bold",
      marginBottom: 3,
      color: theme.palette.text.primary,
    },
    addressText: {
      fontSize: 10,
      color: theme.palette.text.primary,
      marginBottom: 2,
    },
    email: {
      fontSize: 10,
      color: theme.palette.text.secondary,
    },
    periodSection: {
      marginBottom: 20,
    },
    periodLabel: {
      fontSize: 9,
      color: theme.palette.text.secondary,
      marginBottom: 3,
    },
    periodValue: {
      fontSize: 11,
      fontWeight: "bold",
      color: theme.palette.text.primary,
    },
    table: {
      marginTop: 10,
    },
    tableHeader: {
      flexDirection: "row",
      backgroundColor: theme.palette.mode === 'dark' 
        ? theme.palette.grey[800] 
        : theme.palette.grey[100],
      padding: 8,
      fontWeight: "bold",
      borderRadius: 4,
    },
    tableRow: {
      flexDirection: "row",
      padding: 8,
      borderBottom: 1,
      borderColor: theme.palette.divider,
    },
    tableTotalRow: {
      flexDirection: "row",
      padding: 8,
      backgroundColor: theme.palette.mode === 'dark' 
        ? theme.palette.grey[800] 
        : theme.palette.grey[100],
      fontWeight: "bold",
      borderTop: 2,
      borderColor: theme.palette.text.primary,
    },
    col1: {
      width: "20%",
    },
    col2: {
      width: "15%",
      textAlign: "center",
    },
    col3: {
      width: "20%",
      textAlign: "right",
    },
    col4: {
      width: "20%",
      textAlign: "center",
    },
    col5: {
      width: "25%",
      textAlign: "right",
    },
    grandTotalSection: {
      marginTop: 20,
      alignItems: "flex-end",
    },
    grandTotalRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      width: 250,
      paddingVertical: 8,
    },
    grandTotalLabel: {
      fontSize: 16,
      fontWeight: "bold",
      color: theme.palette.text.primary,
    },
    grandTotalValue: {
      fontSize: 16,
      fontWeight: "bold",
      color: theme.palette.success.main,
    },
    footer: {
      marginTop: 30,
      padding: 15,
      backgroundColor: theme.palette.mode === 'dark' 
        ? theme.palette.grey[800] 
        : theme.palette.grey[100],
      borderRadius: 4,
    },
    footerLabel: {
      fontSize: 9,
      fontWeight: "bold",
      marginBottom: 3,
      color: theme.palette.text.primary,
    },
    footerText: {
      fontSize: 9,
      color: theme.palette.text.secondary,
    },
    errorText: {
      color: theme.palette.error.main,
    },
    boldText: {
      fontWeight: "bold",
    },
  };

  // Calculate totals
  const totalTransactions = weekData.dailySummaries.reduce(
    (sum, day) => sum + day.transactionCount,
    0
  );
  const totalEarnings = weekData.dailySummaries.reduce(
    (sum, day) => sum + day.totalEarnings,
    0
  );
  const totalPenaltyCount = weekData.dailySummaries.reduce(
    (sum, day) => sum + day.penaltyCount,
    0
  );
  const totalPenaltyAmount = weekData.dailySummaries.reduce(
    (sum, day) => sum + Math.abs(day.penaltyAmount),
    0
  );
  const netTotal = weekData.dailySummaries.reduce(
    (sum, day) => sum + day.netAmount,
    0
  );

  // Super Admin details
  const superAdmin = {
    name: "Zerox Cloud",
    email: "admin@zeroxcloud.com",
    phone: "+91 9876543210",
    address: "123 Business Park, Pune, Maharashtra 411001",
  };

  const formatCurrency = (amount) => `Rs ${amount.toFixed(2)}`;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header with Logo */}
        <View style={styles.header}>
          <View style={styles.logoSection}>
            <Image src={logoSrc} style={styles.logo} />
            <Text style={styles.invoiceTitle}>INVOICE</Text>
          </View>
          <View style={styles.dateSection}>
            <Text style={styles.dateLabel}>Date:</Text>
            <Text style={styles.dateValue}>{invoiceDate}</Text>
          </View>
        </View>

        <View style={styles.divider} />

        {/* From/To Section */}
        <View style={styles.addressSection}>
          <View style={styles.addressBlock}>
            <Text style={styles.addressLabel}>From:</Text>
            <Text style={styles.companyName}>{superAdmin.name}</Text>
            <Text style={styles.addressText}>{superAdmin.address}</Text>
            <Text style={styles.email}>{superAdmin.email}</Text>
            <Text style={styles.email}>{superAdmin.phone}</Text>
          </View>

          <View style={[styles.addressBlock, { alignItems: "flex-end" }]}>
            <Text style={styles.addressLabel}>Billed to:</Text>
            <Text style={styles.companyName}>{vendorData?.name || "Vendor Name"}</Text>
            <Text style={styles.email}>{vendorData?.email || "vendor@example.com"}</Text>
            <Text style={styles.email}>{vendorData?.phoneNumber || "+91 1234567890"}</Text>
          </View>
        </View>

        {/* Period */}
        <View style={styles.periodSection}>
          <Text style={styles.periodLabel}>Period:</Text>
          <Text style={styles.periodValue}>
            {weekData.weekLabel.replace("Week: ", "")}
          </Text>
        </View>

        {/* Table */}
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            <Text style={styles.col1}>Date</Text>
            <Text style={styles.col2}>Transactions</Text>
            <Text style={styles.col3}>Earnings</Text>
            <Text style={styles.col4}>Penalties</Text>
            <Text style={styles.col5}>Net Amount</Text>
          </View>

          {/* Table Rows */}
          {weekData.dailySummaries.map((day, index) => (
            <View key={index} style={styles.tableRow}>
              <Text style={styles.col1}>{day.date}</Text>
              <Text style={styles.col2}>{day.transactionCount}</Text>
              <Text style={styles.col3}>{formatCurrency(day.totalEarnings)}</Text>
              <Text style={[styles.col4, day.penaltyCount > 0 && styles.errorText]}>
                {day.penaltyCount > 0
                  ? `${day.penaltyCount} (${formatCurrency(Math.abs(day.penaltyAmount))})`
                  : "0"}
              </Text>
              <Text style={styles.col5}>{formatCurrency(day.netAmount)}</Text>
            </View>
          ))}

          {/* Totals Row */}
          <View style={styles.tableTotalRow}>
            <Text style={[styles.col1, styles.boldText]}>Total:</Text>
            <Text style={[styles.col2, styles.boldText]}>{totalTransactions}</Text>
            <Text style={[styles.col3, styles.boldText]}>{formatCurrency(totalEarnings)}</Text>
            <Text style={[styles.col4, styles.boldText, totalPenaltyCount > 0 && styles.errorText]}>
              {totalPenaltyCount > 0
                ? `${totalPenaltyCount} (${formatCurrency(totalPenaltyAmount)})`
                : "0"}
            </Text>
            <Text style={[styles.col5, styles.boldText]}>{formatCurrency(netTotal)}</Text>
          </View>
        </View>

        <View style={styles.divider} />

        {/* Grand Total */}
        <View style={styles.grandTotalSection}>
          <View style={styles.grandTotalRow}>
            <Text style={styles.grandTotalLabel}>Net Total:</Text>
            <Text style={styles.grandTotalValue}>
              {formatCurrency(weekData.totalAmount)}
            </Text>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerLabel}>Note:</Text>
          <Text style={styles.footerText}>
            Thank you for choosing Zerox Cloud! Payment terms: Net 30 days.
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default InvoicePDF;
